package com.dotdrives.web

import kotlinx.browser.window
import kotlinx.coroutines.awaitCancellation
import react.useEffect
import react.useState

private val entityUuid = Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}")

data class EntityRoute(
    val orgId: String? = null,
    val entityId: String? = null,
    val newGiverId: String? = null,
    val newDrive: Boolean = false,
    val initialTemplateId: String? = null,
    val initialPhaseId: String? = null,
) {
    fun newDriveFor(org: String): Boolean = newDrive && (orgId == null || orgId == org)
    fun entityFor(org: String): String? = entityId.takeIf { orgId == org }
    fun newGiverFor(org: String): String? = newGiverId.takeIf { orgId == org }
    fun newTemplateFor(org: String): String? = initialTemplateId.takeIf { orgId == org && newDrive }
    fun newPhaseFor(org: String): String? = initialPhaseId.takeIf { orgId == org && newDrive && initialTemplateId != null }
}

fun parseEntityRoute(hash: String): EntityRoute {
    val scoped = parseOrganizationRoute(hash)
    if (scoped.invalid) return EntityRoute()
    val pairs = hash.substringAfter('?', "").split('&').filter { it.isNotBlank() }
        .map { it.substringBefore('=') to it.substringAfter('=', "") }
    if (pairs.map { it.first }.distinct().size != pairs.size) return EntityRoute()
    val params = pairs.toMap()
    val org = scoped.orgId
    if (params.containsKey("org") && org == null) return EntityRoute()
    return EntityRoute(
        orgId = org,
        entityId = params["id"]?.takeIf { org != null && entityUuid.matches(it) },
        newGiverId = params["new_giver"]?.takeIf { org != null && entityUuid.matches(it) },
        newDrive = params["new"] == "1",
        initialTemplateId = params["template"]?.takeIf { org != null && entityUuid.matches(it) },
        initialPhaseId = params["phase"]?.takeIf { org != null && entityUuid.matches(it) },
    )
}

fun entityRouteHash(route: AppRoute, org: String, id: String, newDrive: Boolean = false): String {
    require(entityUuid.matches(org) && entityUuid.matches(id))
    return "${organizationRouteHash(org, route.hash)}?${if (newDrive) "new_giver" else "id"}=$id"
}

fun newDrivePhaseRouteHash(org: String, template: String, phase: String): String {
    require(listOf(org, template, phase).all(entityUuid::matches))
    return "${organizationRouteHash(org, AppRoute.Drives.hash)}?new=1&template=$template&phase=$phase"
}

fun useHashLocation(): String {
    val (hash, setHash) = useState(window.location.hash)
    useEffect(Unit) {
        val listener: (dynamic) -> Unit = { setHash(window.location.hash) }
        window.asDynamic().addEventListener("hashchange", listener)
        try {
            awaitCancellation()
        } finally {
            window.asDynamic().removeEventListener("hashchange", listener)
        }
    }
    return hash
}
